import { useEffect, useRef, useState } from 'react';
import type { PartItem } from './partsRun';
// Side-effect import: configures the shared PDF.js worker.
import { loadPdf } from './pdfRender';
import type { HeroBox } from './realImages';

// How wide the page is rendered before a box is cut out of it. The PDF is vector, so
// this is purely how many pixels the crop ends up with.
export const PAGE_WIDTH = 1600;
// What the parts table renders at: its crop is zoomed, and it is the picture every
// generation of that part is drawn from.
export const PAGE_WIDTH_LARGE = 2400;

// WHETHER THERE IS A DRAWING TO CUT OUT AT ALL.
//
// A part read off the supplied-parts page always has a box. A TOOL THE STEPS CALL FOR AND
// THE BOX DOES NOT CONTAIN has none — it is on the parts list because a step needs it, and
// there is nothing of it on the page the boxes are measured against, so the read gives it
// [0,0,0,0]. Cut anyway, that becomes a 1.5% sliver of the page's top-left corner: a
// thumbnail of whitespace, indistinguishable from a crop that went wrong.
export const hasBox = (bbox: [number, number, number, number] | undefined | null) =>
  !!bbox && bbox[2] - bbox[0] > 0.001 && bbox[3] - bbox[1] > 0.001;

// A CUT, AND WHERE THE PART IS INSIDE IT. The picture alone stopped being enough the moment
// the cut grew past the box: a crop with a screwdriver and the nut it is driving in it is a
// picture of two parts, and which of them the row is about has to be drawn on.
//
// `inner` is the box again, in the CROP's own 0..1 space — so it needs nothing but the
// picture to be drawn against, at any size it is shown (see BoxOverlay).
export type FoundCrop = {
  src: string;
  inner: HeroBox;
  // WHERE THE CUT WAS TAKEN FROM, on the page, normalized like everything else here. It is
  // what lets a SECOND box be drawn on this crop: the parts a tool is working on have their
  // own boxes on the same page, and mapping one into this picture is nothing but this
  // rectangle. Without it a caller would have to invert `inner`, which is the same
  // arithmetic done twice and wrong once.
  region: HeroBox;
};

// Crop a normalized bbox (x0,y0,x1,y1 in 0..1) out of a rendered page canvas,
// with a little margin to absorb bounding-box imprecision, as a PNG data URL.
//
// THREE MARGINS, and the widest wins:
// - `margin`, a fraction of the PAGE, which is the old one and absorbs an imprecise box.
// - `context`, a multiple of the BOX, for a caller that wants the part's NEIGHBOURS in the
//   frame — one box-width each side puts what the part is being fitted to beside it.
// - `minSpan`, a fraction of the page's width, as a floor on both axes: a wood button is a
//   fiftieth of a page, so a multiple of it is still nothing, and "in context" for a part
//   that small means an absolute neighbourhood rather than a proportional one.
//
// Defaults reproduce the plain cut exactly: a caller that asks for no context gets what it
// always got, which matters because one of them feeds an image generator.
export function cropCanvas(
  src: HTMLCanvasElement,
  bbox: [number, number, number, number],
  margin = 0.015,
  context = 0,
  minSpan = 0,
  // A SECOND RECTANGLE THE CUT MUST HOLD. For the crop whose point is a relationship: a
  // screw is being driven by a screwdriver, and a picture of the screw alone answers a
  // question nobody asked. A fixed amount of context cannot do this — how far away the other
  // thing is drawn is a fact about the page, not a constant — so the caller names it and the
  // frame grows to reach it.
  include?: [number, number, number, number] | null,
): FoundCrop {
  const [x0, y0, x1, y1] = bbox;
  const px0 = x0 * src.width;
  const py0 = y0 * src.height;
  const bw = Math.max(1, (x1 - x0) * src.width);
  const bh = Math.max(1, (y1 - y0) * src.height);
  const floor = minSpan * src.width;
  const padX = Math.max(margin * src.width, context * bw, (floor - bw) / 2);
  const padY = Math.max(margin * src.height, context * bh, (floor - bh) / 2);
  // Clamped to the page, which is why `inner` is measured afterwards rather than assumed
  // centred: a part against the top edge gets its context all on one side.
  // The partner keeps a margin of its own, in the same units, so it is not flush with the
  // edge of the picture.
  const near = include
    ? {
      x0: include[0] * src.width - padX * 0.4, y0: include[1] * src.height - padY * 0.4,
      x1: include[2] * src.width + padX * 0.4, y1: include[3] * src.height + padY * 0.4,
    }
    : null;
  const sx = Math.max(0, Math.min(px0 - padX, near?.x0 ?? Infinity));
  const sy = Math.max(0, Math.min(py0 - padY, near?.y0 ?? Infinity));
  const sw = Math.min(src.width, Math.max(px0 + bw + padX, near?.x1 ?? -Infinity)) - sx;
  const sh = Math.min(src.height, Math.max(py0 + bh + padY, near?.y1 ?? -Infinity)) - sy;
  const out = document.createElement('canvas');
  out.width = Math.max(1, Math.round(sw));
  out.height = Math.max(1, Math.round(sh));
  const ctx = out.getContext('2d');
  const inner: HeroBox = [
    (px0 - sx) / sw, (py0 - sy) / sh, (px0 + bw - sx) / sw, (py0 + bh - sy) / sh,
  ];
  const region: HeroBox = [
    sx / src.width, sy / src.height, (sx + sw) / src.width, (sy + sh) / src.height,
  ];
  if (!ctx) return { src: '', inner, region };
  ctx.drawImage(src, sx, sy, sw, sh, 0, 0, out.width, out.height);
  return { src: out.toDataURL('image/png'), inner, region };
}

// ONE BOX, CUT OUT OF A PICTURE AND BLOWN UP — for a call that asks about what is inside it
// and must not be asked to find it first.
//
// The photograph has to be same-origin to be read back off a canvas at all (see
// listingImageSrc, which is what the proxy route exists for); a cross-origin one taints the
// canvas and toDataURL throws.
//
// SCALED UP where the box is small, which is where this matters. A side rail is eight percent
// of a listing photograph, so at native size it reaches a model as a hundred-pixel smear
// however many megapixels the original had; a crop enlarged to `max` gets that same smear
// spread over the frame, and a vision model spends its tiles on the part instead of on the
// chair. No detail is invented by the enlargement — what changes is how much of the model's
// attention the part is worth.
const loadImage = (src: string): Promise<HTMLImageElement | null> => new Promise((resolve) => {
  const img = new Image();
  img.onload = () => resolve(img);
  img.onerror = () => resolve(null);
  img.src = src;
});

// The rectangle, cut and blown up, as a canvas — everything above the data URL, so a caller
// that wants to DRAW on the crop before sending it works from the same pixels the plain cut
// produces rather than from a second implementation of it.
function drawCrop(
  img: HTMLImageElement, bbox: [number, number, number, number], max: number, grow = 4,
): HTMLCanvasElement | null {
  const [x0, y0, x1, y1] = bbox;
  const sx = Math.max(0, x0) * img.naturalWidth;
  const sy = Math.max(0, y0) * img.naturalHeight;
  const sw = Math.max(1, Math.min(1, x1) * img.naturalWidth - sx);
  const sh = Math.max(1, Math.min(1, y1) * img.naturalHeight - sy);
  // Never past `grow` times its own pixels — four by default: beyond that it is a blur
  // pretending to be a photograph, and every one of those pixels is billed. A caller showing
  // exactly what a server cut passes 1.
  const scale = Math.min(grow, max / Math.max(sw, sh));
  const out = document.createElement('canvas');
  out.width = Math.max(1, Math.round(sw * scale));
  out.height = Math.max(1, Math.round(sh * scale));
  const ctx = out.getContext('2d');
  if (!ctx) return null;
  ctx.drawImage(img, sx, sy, sw, sh, 0, 0, out.width, out.height);
  return out;
}

const asJpeg = (canvas: HTMLCanvasElement): string | null => {
  try {
    return canvas.toDataURL('image/jpeg', 0.9);
  } catch {
    return null;
  }
};

// THE PICTURE WITH OUR RECTANGLES ON IT — a step drawing, numbered where we think a piece of
// hardware is, for the call that is asked which part each number is.
//
// DRAWN RATHER THAN DESCRIBED. The alternative is four coordinates per rectangle in the words,
// and a model reading "0.41, 0.28, 0.46, 0.33" back onto a picture is doing arithmetic on an
// image — the one thing a vision model is not for. A red box with a number in the corner is
// the same claim made in the medium the answer is read in.
//
// Red, and only red: the manuals are black line drawings, so one saturated colour is
// unambiguous without a legend, and the number sits in the box's own top-left corner where it
// cannot be read as belonging to the neighbour.
export async function numberBoxes(
  src: string, boxes: { n: number; bbox: [number, number, number, number] }[], max = 1600,
): Promise<string | null> {
  const img = await loadImage(src);
  if (!img) return null;
  const scale = Math.min(1, max / Math.max(img.naturalWidth, img.naturalHeight));
  const out = document.createElement('canvas');
  out.width = Math.max(1, Math.round(img.naturalWidth * scale));
  out.height = Math.max(1, Math.round(img.naturalHeight * scale));
  const ctx = out.getContext('2d');
  if (!ctx) return null;
  // White underneath, as everywhere else here: a line drawing on transparency flattens to
  // black wherever it is read.
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, out.width, out.height);
  ctx.drawImage(img, 0, 0, out.width, out.height);
  const line = Math.max(2, Math.round(out.width / 500));
  const size = Math.max(11, Math.round(out.width / 55));
  ctx.lineWidth = line;
  ctx.strokeStyle = '#e11d48';
  ctx.fillStyle = '#e11d48';
  ctx.font = `bold ${size}px sans-serif`;
  ctx.textBaseline = 'top';
  for (const { n, bbox } of boxes) {
    const [x0, y0, x1, y1] = bbox;
    const x = x0 * out.width;
    const y = y0 * out.height;
    const w = Math.max(line, (x1 - x0) * out.width);
    const h = Math.max(line, (y1 - y0) * out.height);
    ctx.strokeRect(x, y, w, h);
    const said = String(n);
    const pad = Math.round(size * 0.25);
    const wide = ctx.measureText(said).width + pad * 2;
    // The label sits INSIDE the box at its top-left, so a rectangle near the picture's edge
    // does not lose its number off the frame.
    ctx.fillStyle = '#e11d48';
    ctx.fillRect(x, y, wide, size + pad * 2);
    ctx.fillStyle = '#fff';
    ctx.fillText(said, x + pad, y + pad);
    ctx.fillStyle = '#e11d48';
  }
  return asJpeg(out);
}

export async function cropOut(
  src: string, bbox: [number, number, number, number], max = 640, grow = 4,
): Promise<string | null> {
  const img = await loadImage(src);
  const canvas = img && drawCrop(img, bbox, max, grow);
  return canvas ? asJpeg(canvas) : null;
}

// THE TRACE PASS'S OWN MARGIN, and not the one the boxes are stored with.
//
// The stored padding (_padded_bbox, server side) is sized for SHOWING a box and for sampling
// a colour out of one: a few percent of the box, floored at half a percent of the FRAME. That
// floor is what makes it wrong here — on a wood button an eightieth of the picture wide, half
// a percent each side is a crop two and a half times the part, and "one part fills most of
// it" stops being true of exactly the parts that needed the close-up most.
//
// What a trace wants instead is a margin sized by HOW WRONG THE BOX CAN BE, which is a fact
// about the first call and not about the frame or the part. So: a share of the box, floored
// in the photograph's own pixels, and generous — the boxes come back tight often enough that
// the part's true edge is outside them, and the margin is the only thing that can put that
// edge back in the picture. An edge that is not in the crop cannot be traced; background that
// is is only background.
const TRACE_SHARE = 0.12;
const TRACE_FLOOR_PX = 10;

const traceFrame = (
  bbox: [number, number, number, number], w: number, h: number,
): [number, number, number, number] => {
  const [x0, y0, x1, y1] = bbox;
  const px = Math.max((x1 - x0) * TRACE_SHARE, TRACE_FLOOR_PX / Math.max(1, w));
  const py = Math.max((y1 - y0) * TRACE_SHARE, TRACE_FLOOR_PX / Math.max(1, h));
  return [
    Math.max(0, x0 - px), Math.max(0, y0 - py),
    Math.min(1, x1 + px), Math.min(1, y1 + py),
  ];
};

// How wide the whole-photograph companion goes up. Small: it is there to be recognized, not
// to be traced, and a chair is a chair at 640px. Never enlarged past its own pixels — there
// is nothing in a blown-up photograph that was not in the small one, and every pixel is
// billed once per BOX rather than once per picture.
const WIDE_MAX = 640;

// ONE BOX, CUT FOR TRACING: the crop, the whole photograph it came out of with that box
// drawn on it, and the rectangle the crop was cut from — which the caller needs to map the
// answer back with.
//
// THE SECOND PICTURE IS THE WHOLE SHOT, and it used to be the crop again with its own box on
// it. That said nothing: the crop is cut FROM that box plus a share of it, so the rectangle
// was always the same border inset from the frame by exactly the padding — it could not pick
// one thing out of several, because the frame was derived from it and the two can never
// disagree.
//
// What a tracer actually lacks in a close crop is not where the part is in it but WHAT IT IS
// LOOKING AT: a rear frame arrives as two brown diagonals and a sliver of seat, and "is that
// the rail or the chair's other side" has no answer inside the frame. The whole photograph
// answers it, and on the whole photograph a drawn rectangle finally says something — which of
// the four legs.
//
// MAGENTA, at three pixels: no chair is that colour, so nothing about the line can be read as
// an edge of the furniture.
export async function cropForTrace(
  src: string, bbox: [number, number, number, number], max = 640,
): Promise<{ image: string; hint: string | null; frame: [number, number, number, number] } | null> {
  const img = await loadImage(src);
  if (!img) return null;
  const frame = traceFrame(bbox, img.naturalWidth, img.naturalHeight);
  const crop = drawCrop(img, frame, max);
  const image = crop && asJpeg(crop);
  if (!image) return null;

  // The whole picture at its own size or 640, whichever is smaller: `drawCrop` enlarges a
  // small source towards `max`, which is right for a crop of a rail and pointless for a
  // photograph of a chair.
  const longest = Math.max(img.naturalWidth, img.naturalHeight);
  const wide = drawCrop(img, [0, 0, 1, 1], Math.min(WIDE_MAX, longest));
  const ctx = wide?.getContext('2d');
  if (!wide || !ctx) return { image, hint: null, frame };
  ctx.strokeStyle = '#ff00ff';
  ctx.lineWidth = 3;
  ctx.strokeRect(
    bbox[0] * wide.width,
    bbox[1] * wide.height,
    (bbox[2] - bbox[0]) * wide.width,
    (bbox[3] - bbox[1]) * wide.height,
  );
  return { image, hint: asJpeg(wide), frame };
}

// THE SAME PICTURE, SMALL ENOUGH TO POST — for the calls that send a part's drawing along
// with the question rather than showing it.
//
// The table's crops are cut from a page rendered 2400px wide, because that crop is zoomed
// and is what every generation of the part is drawn from. A model asked "is this the thing
// in that photograph" needs a silhouette, and a silhouette survives 256px; the difference
// is a megabyte on the wire, once per photograph, per part.
//
// Returns the original if it is already small or if anything goes wrong: a drawing that
// arrives slightly too large is better than a question that arrives without it.
export function shrinkDataUrl(url: string, max = 256): Promise<string> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, max / Math.max(img.width, img.height));
      if (scale >= 1) return resolve(url);
      const out = document.createElement('canvas');
      out.width = Math.max(1, Math.round(img.width * scale));
      out.height = Math.max(1, Math.round(img.height * scale));
      const ctx = out.getContext('2d');
      if (!ctx) return resolve(url);
      // White underneath: these are line drawings on transparency, and a transparent PNG
      // flattens to black wherever it is read — a black square is not a silhouette.
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, out.width, out.height);
      ctx.drawImage(img, 0, 0, out.width, out.height);
      resolve(out.toDataURL('image/png'));
    };
    img.onerror = () => resolve(url);
    img.src = url;
  });
}

// Crop one bounding box straight out of the PDF (loads the doc, renders that page
// large, crops). For a single crop on demand — a part, or the finished-product
// picture the splash uses; the parts table renders many crops with its own cached
// page canvases instead.
export async function renderCrop(
  file: Blob,
  page: number,
  bbox: [number, number, number, number],
): Promise<string | null> {
  const buf = await file.arrayBuffer();
  const task = loadPdf(buf);
  const doc = await task.promise;
  try {
    const pdfPage = await doc.getPage(page);
    const base = pdfPage.getViewport({ scale: 1 });
    const viewport = pdfPage.getViewport({ scale: 1600 / base.width });
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    canvas.width = Math.floor(viewport.width);
    canvas.height = Math.floor(viewport.height);
    await pdfPage.render({ canvas, canvasContext: ctx, viewport }).promise;
    return cropCanvas(canvas, bbox).src;
  } catch {
    return null;
  } finally {
    void task.destroy();
  }
}

export const renderPartCrop = (file: Blob, item: PartItem) => renderCrop(file, item.page, item.bbox);

// The crop of every supplied part, re-rendered whenever the parts change — the same
// pictures beside the same parts in the parts table, the repartition and the step cards,
// from one definition rather than the same effect written out in each panel.
//
// `width` is what the PAGE is rendered at before the box is cut out of it, and it is the
// only thing separating a thumbnail from a picture: the step cards want a 56px tile, the
// parts table's crop is zoomed and is also what every generation of that part is drawn
// from. Rendering everything at the larger size would be 2.25x the pixels for a row of
// thumbnails, so the caller says which it is.
// THE SAME CUTTING, FOR THE BOXES FOUND IN THE STEPS. One pass over the pages with the
// canvases cached, exactly as the parts' own drawings are cut — a manual has half a dozen
// step pages and a part can be drawn on any of them, so cutting each crop with its own
// renderCrop would load and rasterise the document once per picture.
//
// Keyed by part index and answering a list per part, in the order the boxes were found.
export function useFoundCrops(
  file: Blob, byPart: Record<number, { page: number; bbox: [number, number, number, number] }[]>,
  width = PAGE_WIDTH,
  // How much of what is around the part to keep, and the smallest a crop may be. Both go
  // straight to cropCanvas, where they are argued; 0 and 0 is the bare box.
  context = 0,
  minSpan = 0,
  // Per crop, in the same order as `byPart`: a second box that crop's cut must reach. See
  // cropCanvas — this is where a screw's picture grows to hold the screwdriver in it.
  also?: Record<number, ([number, number, number, number] | null)[]>,
): Record<number, (FoundCrop | null)[]> {
  const [crops, setCrops] = useState<Record<number, (FoundCrop | null)[]>>({});
  // The BOXES decide when to re-cut, not the object holding them: the store hands back a
  // fresh object on every patch, and a dependency on that would re-rasterise the manual
  // every time a run ticked its progress.
  const key = JSON.stringify(byPart);
  const alsoKey = JSON.stringify(also ?? {});
  useEffect(() => {
    let cancelled = false;
    const entries = Object.entries(byPart);
    if (!entries.length) {
      setCrops({});
      return () => { cancelled = true; };
    }
    void (async () => {
      const buf = await file.arrayBuffer();
      const task = loadPdf(buf);
      const pages = new Map<number, HTMLCanvasElement>();
      try {
        const doc = await task.promise;
        const out: Record<number, (FoundCrop | null)[]> = {};
        for (const [at, list] of entries) {
          out[Number(at)] = [];
          let k = -1;
          for (const found of list) {
            k += 1;
            try {
              let canvas = pages.get(found.page);
              if (!canvas) {
                const pdfPage = await doc.getPage(found.page);
                const base = pdfPage.getViewport({ scale: 1 });
                const viewport = pdfPage.getViewport({ scale: width / base.width });
                canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
                if (!ctx) throw new Error('no 2d context');
                canvas.width = Math.floor(viewport.width);
                canvas.height = Math.floor(viewport.height);
                await pdfPage.render({ canvas, canvasContext: ctx, viewport }).promise;
                pages.set(found.page, canvas);
              }
              out[Number(at)].push(cropCanvas(
                canvas, found.bbox, 0.015, context, minSpan, also?.[Number(at)]?.[k] ?? null,
              ));
            } catch {
              out[Number(at)].push(null);
            }
          }
        }
        if (!cancelled) setCrops(out);
      } finally {
        void task.destroy();
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file, key, alsoKey, width, context, minSpan]);
  return crops;
}

export function usePartCrops(file: Blob, items: PartItem[], width = PAGE_WIDTH): (string | null)[] {
  const [crops, setCrops] = useState<(string | null)[]>([]);
  // Only a NEW document blanks them. When one part's box changes (a redo in the table),
  // the old crops stay on screen until their re-render swaps them in, so the whole row
  // does not blink because one cell was reloaded.
  const fileRef = useRef<Blob | null>(null);
  useEffect(() => {
    let cancelled = false;
    if (fileRef.current !== file) {
      fileRef.current = file;
      setCrops([]);
    }
    void renderPartCrops(file, items, width).then((cs) => {
      if (!cancelled) setCrops(cs);
    });
    return () => {
      cancelled = true;
    };
  }, [file, items, width]);
  return crops;
}

export async function renderPartCrops(
  file: Blob,
  items: PartItem[],
  width = PAGE_WIDTH,
): Promise<(string | null)[]> {
  const buf = await file.arrayBuffer();
  const task = loadPdf(buf);
  const doc = await task.promise;
  const pageCache = new Map<number, HTMLCanvasElement>();
  const renderPage = async (n: number) => {
    const hit = pageCache.get(n);
    if (hit) return hit;
    const pdfPage = await doc.getPage(n);
    const base = pdfPage.getViewport({ scale: 1 });
    const viewport = pdfPage.getViewport({ scale: width / base.width });
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('no 2d context');
    canvas.width = Math.floor(viewport.width);
    canvas.height = Math.floor(viewport.height);
    await pdfPage.render({ canvas, canvasContext: ctx, viewport }).promise;
    pageCache.set(n, canvas);
    return canvas;
  };
  try {
    const out: (string | null)[] = [];
    for (const item of items) {
      try {
        out.push(hasBox(item.bbox) ? cropCanvas(await renderPage(item.page), item.bbox).src : null);
      } catch {
        out.push(null);
      }
    }
    return out;
  } finally {
    void task.destroy();
  }
}
